import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { dedupedRequest, invalidateCacheByUrl } from '../../../utils/axios';
// import EditIcon from '../../icons/EditIcon';

interface UserModalProps {
  show: boolean;
  user: ClientUser | null;
  onClose: () => void;
  onSave: () => void;
}

interface ClientUser {
  email: string;
  name: string | null;
  role: string | null;
  client: any | null;
}

const UserModal = ({ show, user, onClose, onSave }: UserModalProps) => {
  const [email, setEmail] = useState(user?.email || '');
  const [username, setUsername] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>(user?.role ?? 'client');

  useEffect(() => {
    setSelectedRole(user?.role ?? "client");
    setEmail(user?.email ?? "");
    setUsername(user?.name ?? "");
  }, [user, show]);

  const updateRole = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRole(e.currentTarget.value);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!user) {
        throw new Error("No user provided for saving");
      }
      await dedupedRequest({
        method: 'POST',
        url: `/admin/client/user`,
        data: { email: email, name: username, role: selectedRole, client_id: user.client?.id },
      });
        invalidateCacheByUrl(`/admin/client/${user.client?.id}/user`);
        onSave();
        onClose();
    } catch (err) {
      console.error("Failed to save user", err);
      setError("Fehler beim Speichern des Benutzers");
      console.error(error);
    } finally { 
        setSaving(false);
    }
  };

  const isValidEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Neuen Benutzer für {user?.client?.name}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control value={username} onChange={e => setUsername(e.target.value)} required />
          </Form.Group>
          <Form.Group>
            <Form.Label>E-Mail Adresse</Form.Label>
            <Form.Control isValid={email.match(isValidEmail) !== null} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </Form.Group>
          <Form.Group>
            <Form.Label>Rolle</Form.Label>
            <div className="mb-3">
              <Form.Check title="Erstellt einen regulären Benutzer mit Zugriff auf die Dokumente des Kunden"
                inline name="roleGroup" type="radio" id="role-client" onChange={updateRole}
                checked={selectedRole === 'client'} label="client" value="client"
              />
              <Form.Check title="Erstellt einen Test User mit Passwort 'kunde'"
                inline name="roleGroup" type="radio" id="role-test" onChange={updateRole}
                checked={selectedRole === 'test'} label="test" value="test"
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? (<><Spinner size="sm" animation="border" /> Speichern…</>) : 'Speichern'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UserModal;